import styles from "./store-page.module.scss";
import cl from "classnames";
import { observer } from "mobx-react-lite";
import {
  Checkbox,
  IconClose,
  ImagePortrait,
  Input,
  PageContainer,
  PaginationButtons,
  RatingStars,
  Select,
  ValueTag,
} from "@touche-lts/panel-ui/dist";
import { useRootStore } from "mobx-stores/store-provider";
import { useEffect, useState } from "react";
import {
  StoreAttributes,
  StoreAttributeValues,
  StoresSortBy,
} from "models/stores";
import { useParams } from "react-router";
import { useGetIsResponsiveScreen } from "hooks/custom-hooks";

const StorePageComponent = () => {
  const defaultImageSrc =
      "https://st2.depositphotos.com/4403291/7418/v/600/depositphotos_74189661-stock-illustration-online-shop-log.jpg",
    {
      storesPublic: {
        getStoreById,
        paginationInfo,
        setStoreId,
        onPageSelectSingle,
        singlePageStore,
        onSearchStore,
        StoreSortBy,
        selectedStoreSortingSelected,
        setStoreSorting,
        getSingleStoreById,
        getSingleStoreAttributes,
        storeAttributeFilters,
        singlePageStoreInfo,
        handleExpandClick,
        handleCheckboxClick,
        storeFilterAttributes,
        removeFilterCheckBox,
        clearAllFilters,
      },
      routingStore: { push },
    } = useRootStore(),
    [searchStore, setSearchStore] = useState<string>(""),
    { storeId } = useParams<{ storeId: string }>(),
    searchHandle = (search: string) => {
      setSearchStore(search);
      onSearchStore(search, true);
    };

  setStoreId(storeId);

  useEffect(() => {
    getSingleStoreById(storeId);
    getStoreById(storeId, "", { resetPageNumber: true });
    getSingleStoreAttributes(storeId);
  }, [
    getSingleStoreById,
    setStoreId,
    getStoreById,
    storeId,
    getSingleStoreAttributes,
  ]);

  const { isResponsive } = useGetIsResponsiveScreen("(max-width: 1313px)");

  return (
    <PageContainer
      className={cl(
        "container",
        styles.scrollable,
        styles.storePageContainerWrapper,
      )}
    >
      <div className={styles.storePageContainerInner}>
        <div className={styles.storesBackground}></div>
        <div className={styles.storePageContainer}>
          <div className={styles.storeGeneralInfo}>
            <ImagePortrait
              width={116}
              height={166}
              src={singlePageStoreInfo?.backgroundCrop?.url || defaultImageSrc}
            />

            <div className={styles.storeGeneralInfoRating}>
              <h2>{singlePageStoreInfo?.name}</h2>
              <RatingStars
                rating={singlePageStoreInfo?.rating}
                reviewsCnt={singlePageStoreInfo?.reviewsCnt}
              />
            </div>
          </div>
          <hr />
          <div className={`row ${isResponsive && "gap-3"}`}>
            <div className="col-lg-3">
              <div className={styles.storeAttrFilters}>
                {storeAttributeFilters &&
                  storeAttributeFilters.map((attr, attrIndex) => {
                    return (
                      <>
                        <div className={styles.storeArribute}>
                          <p>{attr.name}</p>
                          {!attr.expanded && (
                            <span
                              className={styles.attributePlus}
                              onClick={() => handleExpandClick(attrIndex)}
                            >
                              +
                            </span>
                          )}
                          {attr.expanded && (
                            <span
                              className={styles.attributeMinus}
                              onClick={() => handleExpandClick(attrIndex)}
                            >
                              -
                            </span>
                          )}
                        </div>
                        {attr.expanded &&
                          attr.values.map((value, valueId) => {
                            return (
                              <div className={styles.attributeCheckbox}>
                                <Checkbox
                                  isChecked={value.checked}
                                  onClick={() =>
                                    handleCheckboxClick(attrIndex, valueId)
                                  }
                                />{" "}
                                <span>{value.name}</span>
                              </div>
                            );
                          })}
                        <hr />
                      </>
                    );
                  })}
                <p
                  className={styles.closeAllAttributeFilters}
                  onClick={() => clearAllFilters()}
                >
                  <IconClose /> &nbsp;Close all filters
                </p>
              </div>
            </div>
            <div className="col-lg-9">
              <div className={`row ${isResponsive && "gap-3"}`}>
                <div className="col-lg-8">
                  <Input
                    colorPreset="dark"
                    placeholder="Search for a product..."
                    value={searchStore}
                    onChange={(search) => searchHandle(search)}
                  />
                </div>
                <div className="col-lg-4">
                  <div className={styles.selectStore}>
                    <p className={styles.storeSortBy}>Sort by</p>
                    <Select<StoresSortBy>
                      colorPreset="dark"
                      options={StoreSortBy}
                      noNoneOption
                      value={selectedStoreSortingSelected}
                      placeholder="Actions"
                      className={styles.selectRootClassName}
                      onChange={(
                        type, // TODO-A To add a type
                      ) => setStoreSorting(type, true)}
                    />
                  </div>
                </div>
              </div>
              {!!storeFilterAttributes.length && (
                <div className={styles.valueTagsFilter}>
                  {storeFilterAttributes.map((attr: StoreAttributes) => {
                    return (
                      <>
                        {attr.values
                          .filter((value) => value.checked)
                          .map((value: StoreAttributeValues, index) => {
                            return (
                              <div
                                className={styles.valueSingleTag}
                                key={index}
                              >
                                <ValueTag
                                  key={index}
                                  valueName={value.name}
                                  removable={true}
                                  onRemove={() =>
                                    removeFilterCheckBox(attr.id, value.id)
                                  }
                                />
                              </div>
                            );
                          })}
                      </>
                    );
                  })}
                  <div className={styles.valueTagCloseAll}>
                    <IconClose onClick={() => clearAllFilters()} />
                  </div>
                </div>
              )}
              <div className={styles.storesContainer}>
                {singlePageStore &&
                  singlePageStore.content.map((product, id: number) => {
                    // TODO-A To add model type
                    return (
                      <div
                        className={styles.productInfo}
                        onClick={() => push(`/product/${product.id}`)}
                        key={id}
                      >
                        <div className={styles.productInfoDetails}>
                          <ImagePortrait
                            width={176}
                            height={176}
                            src={
                              !!product?.image
                                ? product?.image?.url
                                : defaultImageSrc
                            }
                          />
                          <div className={styles.productInfoDetailsText}>
                            <h2>{product.name}</h2>
                            <div className={styles.price}>${product.price}</div>
                            <RatingStars
                              rating={product?.rating}
                              reviewsCnt={product?.reviewsCnt}
                            />
                            <small>Add to cart</small>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <PaginationButtons
                colorPreset="dark"
                alwaysShow
                {...paginationInfo}
                onPageSelect={onPageSelectSingle}
              />
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

const StorePage = observer(StorePageComponent);
export { StorePage };
